import { formatQuantity, fromCanonical, ONE, type UnitId, withGrownForm } from "@bl/domain";
import type { RecipeDetail, RecipeLine } from "./api";

/**
 * How a recipe's figures read: gravity to three places, volumes in gallons with barrels beside them,
 * grain in pounds, hops in ounces, temperatures in °F, as White Oak works. The API sends canonical units
 * (liters, grams, °C) and these turn them round at the edge.
 */

/** The unit below each, for an amount under one of it. */
const SMALLER: Partial<Record<UnitId, UnitId>> = {
  lb: "oz",
  kg: "g",
  L: "mL",
  hL: "L",
  bbl: "gal",
};

/**
 * How far short a line is, in the item's unit, or the unit below when it is short by less than one:
 * "1 oz", not "0.06 lb" for a hop line weighed in ounces (the critique, 2026-10-06).
 */
export function showShortBy(canonical: string, unit: UnitId): string {
  const down = SMALLER[unit];
  return formatQuantity(canonical, {
    unit: down && fromCanonical(canonical, unit).lt(ONE) ? down : unit,
  });
}

const LITERS_PER_GALLON = 3.785411784;
const GALLONS_PER_BARREL = 31;
const GRAMS_PER_POUND = 453.59237;
const GRAMS_PER_OUNCE = 28.349523125;

const num = (value: string | number | null | undefined) =>
  value === null || value === undefined ? null : Number(value);

const trim = (value: number, places: number) =>
  value.toLocaleString("en-US", { maximumFractionDigits: places, minimumFractionDigits: 0 });

export const showGravity = (sg: number | null) => (sg === null ? "-" : sg.toFixed(3));

export function showGallons(liters: string | number | null): string {
  const l = num(liters);
  if (l === null) return "-";
  return `${trim(l / LITERS_PER_GALLON, 2)} gal`;
}

export function showBarrels(liters: string | number | null): string {
  const l = num(liters);
  if (l === null) return "-";
  return `${trim(l / LITERS_PER_GALLON / GALLONS_PER_BARREL, 1)} bbl`;
}

/** Grain and sugar in pounds, and ounces under a pound. */
export function showPounds(grams: string | number | null): string {
  const g = num(grams);
  if (g === null) return "-";
  const lb = g / GRAMS_PER_POUND;
  if (lb >= 1) return `${trim(lb, 2)} lb`;
  return `${trim(g / GRAMS_PER_OUNCE, 2)} oz`;
}

/** Hops in ounces, as brewers weigh them. */
export function showOunces(grams: string | number | null): string {
  const g = num(grams);
  if (g === null) return "-";
  return `${trim(g / GRAMS_PER_OUNCE, 2)} oz`;
}

export function showFahrenheit(celsius: string | number | null): string {
  const c = num(celsius);
  if (c === null) return "-";
  return `${trim((c * 9) / 5 + 32, 1)}°F`;
}

export const showPercent = (value: string | number | null, places = 1) => {
  const v = num(value);
  return v === null ? "-" : `${trim(v, places)}%`;
};

/**
 * A beer's color on screen from its SRM, by the usual approximation (red 255 × 0.975^SRM, green
 * 245 × 0.88^SRM, blue 220 × 0.7^SRM): straw at 2, amber at 10, near black by 40.
 */
export function srmColor(srm: number): string {
  const s = Math.max(0, Math.min(srm, 60));
  const channel = (base: number, factor: number) =>
    Math.round(base * factor ** s)
      .toString(16)
      .padStart(2, "0");
  return `#${channel(255, 0.975)}${channel(245, 0.88)}${channel(220, 0.7)}`;
}

export const RECIPE_TYPE_LABELS: Record<string, string> = {
  all_grain: "All grain",
  partial_mash: "Partial mash",
  extract: "Extract",
};

export const HOP_USE_LABELS: Record<string, string> = {
  mash: "Mash",
  first_wort: "First wort",
  boil: "Boil",
  whirlpool: "Whirlpool",
  dry_hop: "Dry hop",
};

const minutesOf = (l: RecipeLine) => Number(l.minutes ?? 0);

/**
 * What a line is called: once linked, its item, as Inventory names it (Scott's Jam, 2026-10-08: "once an
 * item is linked, we don't need to know what it was called in its legacy program"). The name it came
 * with stays on the line, unshown, so the next recipe's line of that name is offered the same item, and
 * it is back when the line is unlinked.
 */
export const lineName = (l: Pick<RecipeLine, "name" | "item">): string =>
  l.item ? withGrownForm(l.item.name, l.item.hopForm) : l.name;

/** Where a line's ingredient is from: once linked, the item's (or nowhere said), not the file's. */
export const lineOrigin = (l: Pick<RecipeLine, "origin" | "item">): string | null =>
  l.item ? l.item.origin : l.origin;

/** A recipe's lines by where they go in, as its page and its printed sheet list them. */
export function ingredientGroups(
  lines: readonly RecipeLine[],
): { title: string; lines: RecipeLine[] }[] {
  const hops = lines.filter((l) => l.kind === "hop");
  return [
    { title: "Mash", lines: lines.filter((l) => l.kind === "fermentable") },
    {
      title: "Boil",
      lines: hops
        .filter((l) => l.hopUse === "boil" || l.hopUse === "first_wort" || l.hopUse === "mash")
        .sort((a, b) => minutesOf(b) - minutesOf(a)),
    },
    { title: "Whirlpool", lines: hops.filter((l) => l.hopUse === "whirlpool") },
    {
      title: "Fermenter",
      lines: [
        ...lines.filter((l) => l.kind === "yeast"),
        ...hops.filter((l) => l.hopUse === "dry_hop"),
      ],
    },
    { title: "Other", lines: lines.filter((l) => l.kind === "other") },
  ].filter((g) => g.lines.length > 0);
}

/**
 * A line's amount, what it is, and its share of the grist or the bitterness, as a recipe's page lists
 * them. Hops are in ounces there, and in pounds on its printed sheet, as White Oak's brew log has them.
 */
export function lineFacts(
  l: RecipeLine,
  hopsIn: "oz" | "lb" = "oz",
): { amount: string; details: string; share: string | null } {
  if (l.kind === "fermentable") {
    return {
      amount: showPounds(l.grams),
      details: `${showPercent(l.yieldPct)} yield, ${Number(l.colorLovibond ?? 0).toFixed(1)}°L`,
      share: l.estimate.gristPct === null ? null : showPercent(l.estimate.gristPct),
    };
  }
  if (l.kind === "hop") {
    const when =
      l.hopUse === "dry_hop"
        ? `${Number(l.days ?? 0).toFixed(0)} days`
        : l.hopUse === "whirlpool"
          ? `${minutesOf(l)} min at ${showFahrenheit(l.tempC)}`
          : l.hopUse === "first_wort" || l.hopUse === "mash"
            ? (HOP_USE_LABELS[l.hopUse] ?? l.hopUse)
            : `${minutesOf(l)} min`;
    return {
      amount: hopsIn === "lb" ? showPounds(l.grams) : showOunces(l.grams),
      details: `${showPercent(l.alphaPct)} alpha, ${when}`,
      share: l.hopUse === "dry_hop" ? null : `${(l.estimate.ibu ?? 0).toFixed(1)} IBU`,
    };
  }
  if (l.kind === "yeast") {
    const att = `${showPercent(l.attenuationPct)} attenuation`;
    // A linked yeast is the item, whose name says its lab; the attenuation stays the recipe's.
    const what = l.item ? [l.item.labCode] : [l.lab, l.productId];
    return {
      amount: l.packages === null ? "-" : `${Number(l.packages).toFixed(1)} pkg`,
      details: [...what, att].filter(Boolean).join(", "),
      share: null,
    };
  }
  return { amount: l.amountText ?? "-", details: l.use ?? "", share: null };
}

/** How a recipe is brewed, as its page's Brewing section and its printed sheet set it out. */
export function brewingFacts(r: RecipeDetail): [string, string][] {
  const s = r.settings;
  const facts: [string, string][] = [
    ...(r.brewSystem ? [["Brew system", r.brewSystem.name] as [string, string]] : []),
    ["Batch", `${showGallons(s.batchLiters)} (${showBarrels(s.batchLiters)})`],
    ["Boil", `${Number(s.boilMinutes)} min`],
    ["Efficiency", showPercent(s.efficiencyPct)],
    ["Boil-off", showGallons(s.boilOffLiters)],
    ["Trub loss", showGallons(s.trubLossLiters)],
    ["Cooling loss", showPercent(s.coolingLossPct)],
    ["Altitude", `${Number(s.altitudeFeet).toLocaleString("en-US")} ft`],
    ["Pre-boil", `${showGallons(r.volumes.preBoilLiters)} at ${showGravity(r.preBoilGravity)}`],
    ["Post-boil", showGallons(r.volumes.postBoilLiters)],
  ];
  // How far the brewer expects it to ferment, where they said, in place of the yeast's (Scott, 2026-10-02).
  if (r.expectedAttenuationPct !== null) {
    facts.push(["Expected attenuation", showPercent(r.expectedAttenuationPct)]);
  } else if (r.expectedFg !== null) {
    const att = r.estimates.attenuationPct;
    facts.push([
      "Expected FG",
      `${showGravity(Number(r.expectedFg))}${att === null ? "" : ` (${showPercent(att)})`}`,
    ]);
  }
  if (r.carbonationVols !== null) {
    facts.push(["Carbonation", `${Number(r.carbonationVols).toFixed(1)} vols`]);
  }
  // Its times in tanks, which every recipe has (Scott's Jams, 2026-10-08): three in a fermenter, one
  // after another, then the brite tank.
  const days = (n: number) => `${n} ${n === 1 ? "day" : "days"}`;
  facts.push(["Fermentation time", days(r.fermentationDays)]);
  facts.push(["Dry hop time", r.dryHopDays === 0 ? "None" : days(r.dryHopDays)]);
  facts.push(["Lager/crash time", r.lagerCrashDays === 0 ? "None" : days(r.lagerCrashDays)]);
  facts.push([
    "Brite tank time",
    r.briteDays === 0 ? "None, out of the fermenter" : days(r.briteDays),
  ]);
  return facts;
}
